/**
 * TrainingType Service
 * API integration for TrainingType CRUD operations
 */
import apiClient from './apiClient';
import {
  TrainingTypeData,
  TrainingTypeFormData,
  TrainingTypeListResponse,
  TrainingTypeFilters,
  TrainingTypeMinimal,
} from '../types/trainingType';
import type { ImportPreviewResult } from '../types/import';

class TrainingTypeService {
  private baseUrl = '/training-types';

  /**
   * Get paginated list of training types
   */
  async getAll(params?: Record<string, string | number | boolean>): Promise<TrainingTypeListResponse> {
    const queryParams = new URLSearchParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }

    const query = queryParams.toString();
    const response = await apiClient.get(`${this.baseUrl}${query ? `?${query}` : ''}`);
    const backendData = response.data.data || response.data;

    return {
      trainingTypes: backendData.trainingTypes ?? [],
      total: backendData.total ?? 0,
      page: backendData.page ?? 1,
      limit: backendData.limit ?? 20,
      totalPages: backendData.totalPages ?? 1,
    };
  }

  /**
   * Get training type by ID
   */
  async getById(id: number): Promise<TrainingTypeData> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Create new training type
   */
  async create(data: TrainingTypeFormData): Promise<TrainingTypeData> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data.data || response.data;
  }

  /**
   * Update training type by ID
   */
  async update(id: number, data: Partial<TrainingTypeFormData>): Promise<TrainingTypeData> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data.data || response.data;
  }

  /**
   * Delete training type by ID
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Get minimal list for Autocomplete (all active training types)
   */
  async getMinimal(): Promise<TrainingTypeMinimal[]> {
    const response = await apiClient.get(`${this.baseUrl}?minimal=true`);
    const backendData = response.data.data || response.data;
    return backendData.trainingTypes ?? backendData ?? [];
  }

  /**
   * Download import template
   */
  async downloadTemplate(): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/template`, { responseType: 'blob' });
    return response as unknown as Blob;
  }

  /**
   * Preview import from Excel
   */
  async previewImport(file: File): Promise<ImportPreviewResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${this.baseUrl}/import/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data ?? response.data;
  }

  /**
   * Import from Excel
   */
  async importFromExcel(file: File): Promise<{ imported: number; skipped: number; errors: Array<{ row: number; error: string }> }> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${this.baseUrl}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data ?? response.data;
  }

  /**
   * Export to Excel
   */
  async exportToExcel(filters?: TrainingTypeFilters): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (filters?.search) queryParams.append('search', filters.search);
    if (filters?.status) queryParams.append('status', filters.status);
    const query = queryParams.toString();
    const response = await apiClient.get(`${this.baseUrl}/export${query ? `?${query}` : ''}`, { responseType: 'blob' });
    return response as unknown as Blob;
  }
}

export const trainingTypeService = new TrainingTypeService();
export default trainingTypeService;
